import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { authApi } from '../../api/AuthApi';
import { shortUrlApi } from '../../api/ShortUrlApi';
import { useI18n } from '../../i18n/I18nContext';
import { LocaleSwitcher } from '../../components/preferences/LocaleSwitcher';
import { ThemeToggle } from '../../components/preferences/ThemeToggle';

const logoUrl = '/logo512.png';

/** The short-url `type` this page knows how to redeem. */
const RESET_TYPE = 'reset-password';

type LinkState =
  | { status: 'checking' }
  | { status: 'valid'; userId: string }
  | { status: 'invalid' }
  | { status: 'done' };

const inputClass =
  'block w-full rounded-xl border border-slate-200 bg-white py-3 pl-10 pr-4 text-sm text-slate-900 shadow-sm outline-none placeholder:text-slate-400 focus:border-red-400 focus:ring-2 focus:ring-red-100 transition dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500';

function LockIcon() {
  return (
    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
      <svg className="h-4 w-4 text-slate-400" viewBox="0 0 20 20" fill="currentColor">
        <path
          fillRule="evenodd"
          d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z"
          clipRule="evenodd"
        />
      </svg>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  const { t } = useI18n();
  return (
    <div className="flex min-h-screen flex-col justify-center bg-slate-50 px-6 dark:bg-slate-950">
      <div className="mx-auto w-full max-w-md">
        <div className="mb-8 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-600">
              <img src={logoUrl} alt={t('auth.brand.name')} className="h-7 w-7" />
            </div>
            <span className="text-lg font-bold tracking-tight text-slate-900 dark:text-slate-100">
              {t('auth.brand.name')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <LocaleSwitcher />
            <ThemeToggle />
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

export function ResetPasswordPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();

  const [link, setLink] = useState<LinkState>({ status: 'checking' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resolveCode = useCallback(async (value: string) => {
    try {
      const { data } = await shortUrlApi.getByCode(value);
      const shortUrl = data?.data;
      // The service only returns links that are still within their expiry window.
      if (!shortUrl || shortUrl.type !== RESET_TYPE || !shortUrl.refId) {
        setLink({ status: 'invalid' });
        return;
      }
      setLink({ status: 'valid', userId: shortUrl.refId });
    } catch {
      setLink({ status: 'invalid' });
    }
  }, []);

  useEffect(() => {
    if (!code) {
      setLink({ status: 'invalid' });
      return;
    }
    resolveCode(code);
  }, [code, resolveCode]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (link.status !== 'valid') return;

    setError(null);
    if (password.length < 6) {
      setError(t('auth.resetPassword.errors.passwordMinLength'));
      return;
    }
    if (password !== confirm) {
      setError(t('auth.resetPassword.errors.mismatch'));
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await authApi.firstChangePassword({ userId: link.userId, password });

      // Burn the link so it cannot be replayed. The endpoint is authenticated and
      // this visitor has no session, so it rides on the token just issued.
      if (code) {
        await shortUrlApi.expire(code, data?.accessToken).catch(() => {});
      }

      setLink({ status: 'done' });
      setPassword('');
      setConfirm('');
      setTimeout(() => navigate('/login', { replace: true }), 4000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.resetPassword.errors.failed'));
    } finally {
      setSubmitting(false);
    }
  }

  if (link.status === 'checking') {
    return (
      <Shell>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
          {t('auth.resetPassword.checking')}
        </div>
      </Shell>
    );
  }

  if (link.status === 'invalid') {
    return (
      <Shell>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
            {t('auth.resetPassword.invalidTitle')}
          </h2>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            {t('auth.resetPassword.invalidBody')}
          </p>
          <Link
            to="/login"
            className="mt-6 inline-flex w-full items-center justify-center rounded-xl bg-red-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-red-700"
          >
            {t('auth.resetPassword.backToLogin')}
          </Link>
        </div>
      </Shell>
    );
  }

  if (link.status === 'done') {
    return (
      <Shell>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
            {t('auth.resetPassword.successTitle')}
          </h2>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            {t('auth.resetPassword.successBody')}
          </p>
          <Link
            to="/login"
            className="mt-6 inline-flex w-full items-center justify-center rounded-xl bg-red-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-red-700"
          >
            {t('auth.resetPassword.goToLogin')}
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
          {t('auth.resetPassword.title')}
        </h2>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          {t('auth.resetPassword.subtitle')}
        </p>

        <form className="mt-6 space-y-5" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/40 dark:text-rose-300">
              {error}
            </div>
          )}

          <div>
            <label
              htmlFor="new-password"
              className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              {t('auth.resetPassword.passwordLabel')}
            </label>
            <div className="relative">
              <LockIcon />
              <input
                id="new-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('auth.resetPassword.passwordPlaceholder')}
                required
                className={inputClass}
              />
            </div>
            <p className="mt-1.5 text-xs text-slate-400 dark:text-slate-500">
              {t('auth.resetPassword.passwordHint')}
            </p>
          </div>

          <div>
            <label
              htmlFor="confirm-password"
              className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              {t('auth.resetPassword.confirmLabel')}
            </label>
            <div className="relative">
              <LockIcon />
              <input
                id="confirm-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder={t('auth.resetPassword.confirmPlaceholder')}
                required
                className={inputClass}
              />
            </div>
          </div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={showPassword}
              onChange={(e) => setShowPassword(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500 dark:border-slate-600 dark:bg-slate-800"
            />
            <span className="text-sm text-slate-600 dark:text-slate-400">
              {t('auth.resetPassword.showPassword')}
            </span>
          </label>

          <button
            type="submit"
            disabled={submitting}
            className="relative flex w-full items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting && (
              <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            {submitting ? t('auth.resetPassword.submitting') : t('auth.resetPassword.submit')}
          </button>

          <p className="text-center text-sm text-slate-500 dark:text-slate-400">
            {t('auth.resetPassword.rememberedPrompt')}{' '}
            <Link className="font-medium text-red-600 hover:underline" to="/login">
              {t('auth.resetPassword.backToLogin')}
            </Link>
          </p>
        </form>
      </div>
    </Shell>
  );
}
